import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Briefcase, FileCheck, Settings, LogOut, Plus, Search,
  Users, Clock, CreditCard, ChevronRight, ChevronLeft, ChevronDown, ChevronUp,
  Send, CheckCircle, XCircle, Link2, Mail, ArrowLeft, MessageSquare, Eye,
  Upload, DollarSign, Activity, MoreHorizontal, Zap, TrendingUp, FolderOpen,
  Package, Trash2, Image, Video, Mic, FileText, RefreshCw, Copy,
  Download, GitCompare, Archive, AlertCircle, Filter, Edit3, Shield, HelpCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import API from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import SettingsView from '@/pages/AgencySettings';
import FinanceView from '@/pages/AgencyFinance';
import { ProposalTab, LicenseTab, LikenessPackTab, ContentTab, PaymentTab, ActivityTab } from '@/pages/DealTabs';
import { useOnboarding } from '@/hooks/useOnboarding';
import { WelcomeModal } from '@/components/onboarding/WelcomeModal';
import { OnboardingChecklist } from '@/components/onboarding/OnboardingChecklist';
import { ProductTour } from '@/components/onboarding/ProductTour';
import { TOUR_STEPS } from '@/components/onboarding/onboardingConfig';
import ProposalDiffViewer from '@/components/ProposalDiffViewer';
import SupportDialog from '@/components/SupportDialog';

// ── Status Badge ──
function StatusBadge({ status }) {
  const map = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    accepted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    completed: 'bg-slate-50 text-slate-600 border-slate-200',
    pending_payment: 'bg-amber-50 text-amber-700 border-amber-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    proposal_sent: 'bg-blue-50 text-blue-700 border-blue-200',
    sent: 'bg-blue-50 text-blue-700 border-blue-200',
    draft: 'bg-slate-50 text-slate-500 border-slate-200',
    rejected: 'bg-red-50 text-red-700 border-red-200',
    negotiating: 'bg-purple-50 text-purple-700 border-purple-200',
    countered: 'bg-purple-50 text-purple-700 border-purple-200',
    archived: 'bg-slate-50 text-slate-500 border-slate-200',
    none: 'bg-slate-50 text-slate-400 border-slate-200',
    pending_review: 'bg-amber-50 text-amber-700 border-amber-200',
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    changes_requested: 'bg-orange-50 text-orange-700 border-orange-200',
  };
  const label = (status || 'unknown').replace(/_/g, ' ');
  return (
    <Badge variant="outline" data-testid={`status-badge-${status}`}
      className={`text-xs font-medium capitalize ${map[status] || 'bg-slate-50 text-slate-500'}`}>
      {label}
    </Badge>
  );
}

// ── Main Dashboard ──
export default function AgencyDashboard() {
  const { user, logout, hasPermission, teamContext, pendingInvites } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState('dashboard');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [tourRun, setTourRun] = useState(false);
  const [dashRefreshKey, setDashRefreshKey] = useState(0);

  const onboarding = useOnboarding({ onExampleSeeded: () => setDashRefreshKey(k => k + 1) });

  useEffect(() => {
    if (onboarding.data && !onboarding.data.state?.welcome_dismissed) setWelcomeOpen(true);
  }, [onboarding.data]);

  // Whenever the user returns to the Dashboard view, re-check checklist progress.
  useEffect(() => {
    if (view === 'dashboard') onboarding.refetch();
  }, [view, onboarding]);

  const closeWelcome = () => { setWelcomeOpen(false); onboarding.patch({ welcome_dismissed: true }); };
  const startTour = () => { setTourRun(true); };

  // Handle Stripe payment return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const dealId = params.get('deal_id');
    const paymentSuccess = params.get('payment_success');
    const paymentCancelled = params.get('payment_cancelled');

    if (dealId && (paymentSuccess || paymentCancelled)) {
      setSelectedDeal({ id: dealId });
      setView('deal-detail');
      if (paymentCancelled) {
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, []);

  const allNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, perms: [] },
    { id: 'campaigns', label: 'Campaigns', icon: Briefcase, perms: ['view_campaigns'] },
    { id: 'creators', label: 'Creators', icon: Users, perms: ['view_creator_deals'] },
    { id: 'finance', label: 'Finance', icon: DollarSign, perms: ['view_finance'] },
    { id: 'licenses', label: 'Licenses', icon: FileCheck, perms: ['manage_licenses', 'view_creator_deals'] },
    { id: 'settings', label: 'Settings', icon: Settings, perms: [] },
  ];

  // Filter nav items based on permissions (check if user has at least one of the required perms)
  const navItems = allNavItems.filter(item => {
    if (item.perms.length === 0) return true;
    return item.perms.some(p => hasPermission(p));
  });

  const openCampaign = (c) => { setSelectedCampaign(c); setSelectedDeal(null); setSelectedCreator(null); setView('campaign-detail'); };
  const openDeal = (d) => { setSelectedDeal(d); setView('deal-detail'); };
  const openCreator = (c) => { setSelectedCreator(c); setView('creator-detail'); };
  const goBack = () => {
    if (view === 'deal-detail') { setSelectedDeal(null); setView('campaign-detail'); }
    else if (view === 'campaign-detail') { setSelectedCampaign(null); setView('campaigns'); }
    else if (view === 'creator-detail') { setSelectedCreator(null); setView('creators'); }
    else { setView('dashboard'); }
  };

  const isTeamMode = teamContext?.is_team_mode;
  const userRole = teamContext?.role;

  return (
    <div className="flex min-h-screen bg-[#F6F8FC]" data-testid="agency-dashboard">
      <aside className="w-60 bg-white border-r border-slate-200 flex flex-col fixed h-full z-10">
        <div className="p-5 border-b border-slate-100">
          <button onClick={() => navigate('/')} className="font-heading text-lg font-bold text-slate-900 tracking-tight">Mylikeness</button>
          <p className="text-[10px] font-semibold text-blue-600 tracking-widest uppercase mt-0.5">Agency</p>
        </div>
        <nav className="flex-1 p-3 space-y-0.5">
          {navItems.map(item => (
            <button key={item.id} data-testid={`nav-${item.id}`}
              onClick={() => { setView(item.id); setSelectedCampaign(null); setSelectedDeal(null); setSelectedCreator(null); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all
                ${(view === item.id || (item.id === 'campaigns' && (view === 'campaign-detail' || view === 'deal-detail')) || (item.id === 'creators' && view === 'creator-detail'))
                  ? 'bg-blue-50 text-blue-700' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'}`}>
              <item.icon className="w-4 h-4" />{item.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-100">
          <div className="flex items-center gap-2.5 p-2 rounded-lg bg-slate-50 mb-2">
            <Avatar className="w-8 h-8">
              <AvatarFallback className="bg-blue-100 text-blue-700 text-xs font-bold">{user?.display_name?.[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-slate-800 text-xs font-semibold truncate">{user?.display_name}</p>
              <p className="text-slate-400 text-[10px] truncate">
                {isTeamMode && userRole ? `${userRole.replace('_', ' ')} ` : ''}{user?.email}
              </p>
            </div>
          </div>
          <Button data-testid="agency-logout-btn" variant="ghost" size="sm"
            onClick={() => { logout(); navigate('/'); }}
            className="w-full text-slate-400 hover:text-slate-700 text-xs h-8">
            <LogOut className="w-3 h-3 mr-1.5" />Sign Out
          </Button>
        </div>
      </aside>

      <main className="flex-1 ml-60 p-6 overflow-auto">
        {view === 'dashboard' && <DashboardView onOpenCampaign={openCampaign} onNav={setView} onboarding={onboarding} onStartTour={startTour} refreshKey={dashRefreshKey} />}
        {view === 'campaigns' && <CampaignsView onOpenCampaign={openCampaign} />}
        {view === 'campaign-detail' && selectedCampaign && (
          <CampaignDetailView campaign={selectedCampaign} onBack={goBack} onOpenDeal={openDeal} onRefresh={(c) => setSelectedCampaign(c)} />
        )}
        {view === 'deal-detail' && selectedDeal && (
          <DealDetailView dealId={selectedDeal.id} onBack={goBack} />
        )}
        {view === 'creators' && <CreatorsView onOpenCreator={openCreator} />}
        {view === 'creator-detail' && selectedCreator && (
          <CreatorFullDetailView creator={selectedCreator} onBack={goBack} />
        )}
        {view === 'finance' && <FinanceView />}
        {view === 'licenses' && <LicensesView />}
        {view === 'settings' && <SettingsView />}
      </main>

      <WelcomeModal
        open={welcomeOpen}
        role="agency"
        name={user?.display_name}
        onClose={closeWelcome}
        onStartTour={() => { closeWelcome(); startTour(); }}
      />
      <ProductTour
        run={tourRun}
        steps={TOUR_STEPS.agency}
        onClose={() => setTourRun(false)}
        onComplete={() => onboarding.patch({ tour_completed: true })}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ── DASHBOARD VIEW ──
// ═══════════════════════════════════════════════════════════
function DashboardView({ onOpenCampaign, onNav, onboarding, onStartTour, refreshKey }) {
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const ob = onboarding?.data;

  useEffect(() => {
    const load = async () => {
      try {
        const [s, c] = await Promise.all([
          API.get('/buyer/dashboard-stats'),
          API.get('/buyer/campaigns'),
        ]);
        setStats(s.data);
        setCampaigns(c.data.campaigns);
      } catch { toast.error('Failed to load dashboard'); }
    };
    load();
  }, [refreshKey]);

  // Auto-dismiss checklist when all steps are done
  useEffect(() => {
    if (!ob || ob.state?.checklist_dismissed) return;
    const p = ob.progress;
    if (p && p.done >= p.total && p.total > 0) {
      const t = setTimeout(() => onboarding.patch({ checklist_dismissed: true }), 3000);
      return () => clearTimeout(t);
    }
  }, [ob, onboarding]);

  if (!stats) return <LoadingSkeleton />;

  const statCards = [
    { label: 'Active Campaigns', value: stats.active_campaigns, icon: Briefcase, color: 'text-blue-600 bg-blue-50' },
    { label: 'Connected Creators', value: stats.connected_creators, icon: Users, color: 'text-emerald-600 bg-emerald-50' },
    { label: 'Pending Proposals', value: stats.pending_proposals, icon: Clock, color: 'text-amber-600 bg-amber-50' },
    { label: 'Active Licenses', value: stats.active_licenses, icon: FileCheck, color: 'text-violet-600 bg-violet-50' },
  ];

  return (
    <div data-testid="dashboard-view">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Dashboard</h1>
          <p className="text-slate-500 text-sm mt-0.5">Overview of your campaigns and creator partnerships</p>
        </div>
        {ob?.state?.checklist_dismissed && (
          <OnboardingChecklist
            checklist={ob.checklist} progress={ob.progress} role={ob.role}
            dismissed onReopen={() => onboarding.patch({ checklist_dismissed: false })}
          />
        )}
      </div>

      {ob && !ob.state?.checklist_dismissed && (
        <div className="mb-6">
          <OnboardingChecklist
            checklist={ob.checklist} progress={ob.progress} role={ob.role}
            onAction={(t) => onNav(t)}
            onStartTour={onStartTour}
            dismissed={false}
            onDismiss={() => onboarding.patch({ checklist_dismissed: true })}
          />
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {statCards.map(s => (
          <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/ /g, '-')}`}
            className="bg-white rounded-xl border border-slate-100 p-4 hover:shadow-sm transition-shadow">
            <div className={`w-9 h-9 rounded-lg ${s.color} flex items-center justify-center mb-3`}>
              <s.icon className="w-4.5 h-4.5" />
            </div>
            <p className="text-2xl font-bold text-slate-900">{s.value}</p>
            <p className="text-slate-500 text-xs mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 bg-white rounded-xl border border-slate-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Recent Campaigns</h3>
            <Button variant="ghost" size="sm" className="text-blue-600 text-xs h-7" onClick={() => onNav('campaigns')}>
              View All <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
          {campaigns.length === 0 ? (
            <div className="text-center py-10">
              <Briefcase className="w-10 h-10 text-slate-200 mx-auto mb-2" />
              <p className="text-slate-400 text-sm">No campaigns yet</p>
              <Button size="sm" className="mt-3 bg-blue-600 hover:bg-blue-700 text-white text-xs" onClick={() => onNav('campaigns')}>
                <Plus className="w-3 h-3 mr-1" />Create Campaign
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {campaigns.slice(0, 5).map(c => (
                <button key={c.id} onClick={() => onOpenCampaign(c)} data-testid={`campaign-row-${c.id}`}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors text-left group">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Briefcase className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {c.name}
                      {c.is_example && (
                        <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 text-[9px] font-semibold align-middle" data-testid={`campaign-example-badge-${c.id}`}>EXAMPLE</span>
                      )}
                    </p>
                    <p className="text-xs text-slate-400">{c.brand} · {c.deal_count || 0} deals</p>
                  </div>
                  <StatusBadge status={c.status} />
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-xl border border-slate-100 p-5">
            <h3 className="font-semibold text-slate-900 mb-3">Quick Actions</h3>
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start h-9 text-xs border-slate-200" onClick={() => onNav('campaigns')}>
                <Plus className="w-3.5 h-3.5 mr-2 text-blue-600" />New Campaign
              </Button>
              <Button variant="outline" className="w-full justify-start h-9 text-xs border-slate-200" onClick={() => onNav('creators')}>
                <Link2 className="w-3.5 h-3.5 mr-2 text-emerald-600" />Connect Creator
              </Button>
              <Button variant="outline" className="w-full justify-start h-9 text-xs border-slate-200" onClick={() => onNav('licenses')}>
                <FolderOpen className="w-3.5 h-3.5 mr-2 text-violet-600" />License Vault
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-100 p-5">
            <h3 className="font-semibold text-slate-900 mb-1">Total Spend</h3>
            <p className="text-3xl font-bold text-slate-900">${stats.total_spend?.toLocaleString()}</p>
            <p className="text-xs text-slate-400 mt-0.5">Across {stats.active_licenses} active licenses</p>
          </div>

          {stats.recent_deals?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-100 p-5">
              <h3 className="font-semibold text-slate-900 mb-3">Recent Deals</h3>
              <div className="space-y-2">
                {stats.recent_deals.slice(0, 3).map(d => (
                  <div key={d.id} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50">
                    <Avatar className="w-7 h-7">
                      <AvatarImage src={d.creator?.avatar_url} />
                      <AvatarFallback className="bg-blue-100 text-blue-700 text-[10px]">{d.creator?.display_name?.[0]}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-700 truncate">{d.creator?.display_name}</p>
                      <p className="text-[10px] text-slate-400">{d.campaign?.name}</p>
                    </div>
                    <StatusBadge status={d.status} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ── CAMPAIGNS LIST VIEW ──
// ═══════════════════════════════════════════════════════════
function CampaignsView({ onOpenCampaign }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState('newest');
  const [form, setForm] = useState({
    name: '', brand: '', campaign_type: 'social_campaign', description: '', objective: '',
    deliverable_types: [], channels: [], territory: [], references: [],
    start_date: '', end_date: '', status: 'draft', thumbnail_url: '',
    shared_defaults: { proposal_expiry_days: 7, content_approval_required: true, default_payment_model: 'one_off' },
  });
  const [saving, setSaving] = useState(false);
  const [showDefaults, setShowDefaults] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadThumbnail = async (file) => {
    if (!file || !file.type.startsWith('image/')) { toast.error('Please choose an image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await API.post('/uploads/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, thumbnail_url: res.data.url }));
      toast.success('Image uploaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const PLATS = ['Instagram', 'TikTok', 'YouTube', 'Twitter/X', 'Facebook', 'LinkedIn', 'Podcast', 'Website'];
  const TERRS = ['US', 'UK', 'EU', 'Canada', 'APAC', 'LATAM', 'Middle East', 'Global'];
  const DELIVERABLES = ['image_ads', 'short_videos', 'long_form_video', 'stories', 'reels', 'voiceovers', 'promo_stills', 'podcast_spots'];
  const CAMPAIGN_TYPES = [
    { value: 'social_campaign', label: 'Social Campaign' },
    { value: 'ad_commercial', label: 'Ad / Commercial' },
    { value: 'brand_partnership', label: 'Brand Partnership' },
    { value: 'ambassador_program', label: 'Ambassador Program' },
    { value: 'long_term_ongoing', label: 'Long-Term / Ongoing' },
    { value: 'series_episodic', label: 'Series / Episodic' },
    { value: 'custom', label: 'Custom' },
  ];

  const STATUS_FILTERS = [
    { id: 'all', label: 'All' },
    { id: 'active', label: 'Active' },
    { id: 'draft', label: 'Draft' },
    { id: 'pending', label: 'Pending' },
    { id: 'content_review', label: 'Content Review' },
    { id: 'archived', label: 'Archived' },
  ];

  const load = useCallback(async () => {
    try {
      const res = await API.get('/buyer/campaigns');
      setCampaigns(res.data.campaigns);
    } catch { toast.error('Failed to load campaigns'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createCampaign = async (addCreator = false) => {
    if (!form.name.trim() || !form.brand.trim()) { toast.error('Name and brand are required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        shared_defaults: {
          ...form.shared_defaults,
          default_channels: form.channels,
          default_territories: form.territory,
        },
      };
      const res = await API.post('/buyer/campaigns', payload);
      setCampaigns(prev => [res.data.campaign, ...prev]);
      setShowCreate(false);
      setForm({
        name: '', brand: '', campaign_type: 'social_campaign', description: '', objective: '',
        deliverable_types: [], channels: [], territory: [], references: [],
        start_date: '', end_date: '', status: 'draft', thumbnail_url: '',
        shared_defaults: { proposal_expiry_days: 7, content_approval_required: true, default_payment_model: 'one_off' },
      });
      toast.success(addCreator ? 'Campaign created — add your first creator' : 'Campaign created');
      onOpenCampaign(res.data.campaign);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
    finally { setSaving(false); }
  };

  const archiveCampaign = async (e, cId) => {
    e.stopPropagation();
    if (!window.confirm('Archive this campaign?')) return;
    try {
      await API.delete(`/buyer/campaigns/${cId}`);
      toast.success('Campaign archived');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const duplicateCampaign = async (e, cId) => {
    e.stopPropagation();
    try {
      const res = await API.post(`/buyer/campaigns/${cId}/duplicate`);
      toast.success('Campaign duplicated');
      setCampaigns(prev => [res.data.campaign, ...prev]);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // Filter + sort
  const filtered = campaigns.filter(c => {
    if (search) {
      const q = search.toLowerCase();
      if (!(c.name || '').toLowerCase().includes(q) && !(c.brand || '').toLowerCase().includes(q)) return false;
    }
    if (statusFilter === 'all') return c.status !== 'archived';
    if (statusFilter === 'active') return c.status === 'active' && (c.deals_pending || 0) === 0 && (c.deals_content_review || 0) === 0;
    if (statusFilter === 'draft') return c.status === 'draft';
    if (statusFilter === 'archived') return c.status === 'archived';
    if (statusFilter === 'pending') return c.status !== 'archived' && (c.deals_pending || 0) > 0;
    if (statusFilter === 'content_review') return c.status !== 'archived' && (c.deals_content_review || 0) > 0;
    return c.status !== 'archived';
  }).sort((a, b) => {
    if (sort === 'oldest') return (a.created_at || '').localeCompare(b.created_at || '');
    return (b.created_at || '').localeCompare(a.created_at || ''); // newest
  });

  const nonArchived = campaigns.filter(c => c.status !== 'archived');
  const statusCounts = {
    all: nonArchived.length,
    active: nonArchived.filter(c => c.status === 'active' && (c.deals_pending || 0) === 0 && (c.deals_content_review || 0) === 0).length,
    draft: campaigns.filter(c => c.status === 'draft').length,
    pending: nonArchived.filter(c => (c.deals_pending || 0) > 0).length,
    content_review: nonArchived.filter(c => (c.deals_content_review || 0) > 0).length,
    archived: campaigns.filter(c => c.status === 'archived').length,
  };

  return (
    <div data-testid="campaigns-view">
      {showCreate ? (
        /* ═══ FULL PAGE CREATE CAMPAIGN ═══ */
        <div data-testid="create-campaign-page">
          <button onClick={() => setShowCreate(false)}
            className="flex items-center gap-1 text-slate-400 hover:text-slate-700 text-xs font-medium mb-5 transition-colors">
            <ChevronLeft className="w-3.5 h-3.5" />Back to Campaigns
          </button>

          <div className="max-w-3xl">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">New Campaign</h1>
              <p className="text-slate-500 text-sm mt-0.5">Set up the campaign agreement. Creator-specific terms are configured per deal.</p>
            </div>

            {/* Section 1: Basics */}
            <div className="bg-white rounded-xl border border-slate-100 p-5 mb-4">
              <h3 className="font-semibold text-slate-800 text-sm mb-4">Campaign Details</h3>

              {/* Thumbnail upload */}
              <div className="mb-4">
                <Label className="text-xs font-medium text-slate-700 mb-1.5 block">Campaign Image</Label>
                <div className="flex items-center gap-4">
                  <label className="relative w-20 h-20 rounded-xl overflow-hidden bg-slate-100 border-2 border-dashed border-slate-200 hover:border-blue-300 flex items-center justify-center cursor-pointer transition-colors flex-shrink-0"
                    data-testid="campaign-thumbnail-upload">
                    {form.thumbnail_url ? (
                      <img src={form.thumbnail_url.startsWith('http') ? form.thumbnail_url : `${process.env.REACT_APP_BACKEND_URL}${form.thumbnail_url}`}
                        alt="Campaign" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center">
                        {uploading ? <RefreshCw className="w-5 h-5 text-slate-400 animate-spin" /> : <Image className="w-5 h-5 text-slate-400" />}
                      </div>
                    )}
                    <input type="file" accept="image/*" className="hidden" onChange={e => uploadThumbnail(e.target.files?.[0])} />
                  </label>
                  <div>
                    <p className="text-xs text-slate-500">Upload an image for this campaign</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">JPEG, PNG, or WebP. Max 5MB.</p>
                    {form.thumbnail_url && (
                      <button type="button" onClick={() => setForm(f => ({...f, thumbnail_url: ''}))}
                        className="text-[10px] text-red-400 hover:text-red-600 mt-1">Remove image</button>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className="text-xs font-medium text-slate-700 mb-1.5 block">Campaign Name *</Label>
                  <Input data-testid="campaign-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                    placeholder="Summer Launch 2026" className="h-9 text-sm border-slate-200" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-700 mb-1.5 block">Client / Brand *</Label>
                  <Input data-testid="campaign-brand-input" value={form.brand} onChange={e => setForm({...form, brand: e.target.value})}
                    placeholder="Aurora Cosmetics" className="h-9 text-sm border-slate-200" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <Label className="text-xs font-medium text-slate-700 mb-1.5 block">Campaign Type</Label>
                  <Select value={form.campaign_type} onValueChange={v => setForm({...form, campaign_type: v})}>
                    <SelectTrigger className="h-9 text-sm border-slate-200" data-testid="campaign-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {CAMPAIGN_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-700 mb-1.5 block">Start Date</Label>
                  <Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="h-9 text-sm border-slate-200" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-700 mb-1.5 block">End Date</Label>
                  <Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} className="h-9 text-sm border-slate-200" />
                </div>
              </div>
              <div className="mb-4">
                <Label className="text-xs font-medium text-slate-700 mb-1.5 block">Brief / Description</Label>
                <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                  placeholder="What is this campaign about..." rows={3} className="text-sm border-slate-200" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-700 mb-1.5 block">Objective</Label>
                <Input value={form.objective} onChange={e => setForm({...form, objective: e.target.value})}
                  placeholder="Drive brand awareness for spring collection" className="h-9 text-sm border-slate-200" />
              </div>
            </div>

            {/* Section 2: Scope */}
            <div className="bg-white rounded-xl border border-slate-100 p-5 mb-4">
              <h3 className="font-semibold text-slate-800 text-sm mb-4">Scope & Distribution</h3>
              <div className="mb-4">
                <Label className="text-xs font-medium text-slate-700 mb-2 block">Channels</Label>
                <div className="flex flex-wrap gap-2">
                  {PLATS.map(p => (
                    <button key={p} type="button" onClick={() => setForm(f => ({...f, channels: f.channels.includes(p) ? f.channels.filter(x=>x!==p) : [...f.channels, p]}))}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all
                        ${form.channels.includes(p) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300'}`}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mb-4">
                <Label className="text-xs font-medium text-slate-700 mb-2 block">Deliverable Types</Label>
                <div className="flex flex-wrap gap-2">
                  {DELIVERABLES.map(d => (
                    <button key={d} type="button" onClick={() => setForm(f => ({...f, deliverable_types: f.deliverable_types.includes(d) ? f.deliverable_types.filter(x=>x!==d) : [...f.deliverable_types, d]}))}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all capitalize
                        ${form.deliverable_types.includes(d) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300'}`}>
                      {d.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-700 mb-2 block">Territory</Label>
                <div className="flex flex-wrap gap-2">
                  {TERRS.map(t => (
                    <button key={t} type="button" onClick={() => setForm(f => ({...f, territory: f.territory.includes(t) ? f.territory.filter(x=>x!==t) : [...f.territory, t]}))}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all
                        ${form.territory.includes(t) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300'}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Section 3: Shared Defaults (collapsible) */}
            <div className="bg-white rounded-xl border border-slate-100 p-5 mb-6">
              <button onClick={() => setShowDefaults(!showDefaults)} type="button"
                className="flex items-center justify-between w-full">
                <div>
                  <h3 className="font-semibold text-slate-800 text-sm">Shared Defaults</h3>
                  <p className="text-[10px] text-slate-400 mt-0.5">Pre-fill values for future creator deals in this campaign</p>
                </div>
                {showDefaults ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>
              {showDefaults && (
                <div className="mt-4 pt-4 border-t border-slate-100 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-slate-600 mb-1.5 block">Proposal Expiry (days)</Label>
                      <Input type="number" value={form.shared_defaults.proposal_expiry_days}
                        onChange={e => setForm(f => ({...f, shared_defaults: {...f.shared_defaults, proposal_expiry_days: parseInt(e.target.value) || 7}}))}
                        className="h-9 text-sm border-slate-200" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600 mb-1.5 block">Default Payment Model</Label>
                      <Select value={form.shared_defaults.default_payment_model}
                        onValueChange={v => setForm(f => ({...f, shared_defaults: {...f.shared_defaults, default_payment_model: v}}))}>
                        <SelectTrigger className="h-9 text-sm border-slate-200"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="one_off">One-Off</SelectItem>
                          <SelectItem value="milestone">Milestone</SelectItem>
                          <SelectItem value="recurring">Recurring</SelectItem>
                          <SelectItem value="renewal">Renewal-Based</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-slate-600">
                    <input type="checkbox" checked={form.shared_defaults.content_approval_required}
                      onChange={e => setForm(f => ({...f, shared_defaults: {...f.shared_defaults, content_approval_required: e.target.checked}}))}
                      className="rounded border-slate-300" />
                    Content approval required by default
                  </label>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pb-8">
              <Button data-testid="save-campaign-draft-btn" variant="outline" onClick={() => createCampaign(false)} disabled={saving}
                className="border-slate-200 text-sm h-10 px-6">
                {saving ? 'Saving...' : 'Save as Draft'}
              </Button>
              <Button data-testid="save-campaign-btn" onClick={() => { setForm(f => ({...f, status: 'active'})); setTimeout(() => createCampaign(false), 0); }} disabled={saving}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm h-10">
                {saving ? 'Creating...' : 'Create Campaign'}
              </Button>
              <Button data-testid="save-campaign-add-creator-btn" onClick={() => { setForm(f => ({...f, status: 'active'})); setTimeout(() => createCampaign(true), 0); }} disabled={saving}
                variant="outline" className="border-blue-200 text-blue-600 hover:bg-blue-50 text-sm h-10 px-6">
                Create & Add Creator
              </Button>
            </div>
          </div>
        </div>
      ) : (
        /* ═══ CAMPAIGNS LIST ═══ */
        <>
          <div className="flex items-center justify-between mb-5">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Campaigns</h1>
              <p className="text-slate-500 text-sm mt-0.5">{campaigns.length} total campaigns</p>
            </div>
            <Button data-testid="create-campaign-btn" onClick={() => setShowCreate(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-9 px-4">
              <Plus className="w-3.5 h-3.5 mr-1.5" />New Campaign
            </Button>
          </div>

          {/* Filter pills */}
          <div className="flex items-center gap-2 mb-4 overflow-x-auto" data-testid="campaign-status-filters">
            {STATUS_FILTERS.map(f => (
              <button key={f.id} data-testid={`filter-${f.id}`} onClick={() => setStatusFilter(f.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap ${
                  statusFilter === f.id
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300'
                }`}>
                {f.label}
                {statusCounts[f.id] > 0 && <span className="ml-1.5 text-[10px] opacity-70">{statusCounts[f.id]}</span>}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input data-testid="campaign-search" placeholder="Search campaigns..." value={search} onChange={e => setSearch(e.target.value)}
                className="pl-9 bg-white border-slate-200 h-9 text-sm" />
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="campaign-sort" className="w-36 h-9 text-xs bg-white border-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="newest">Newest first</SelectItem>
                <SelectItem value="oldest">Oldest first</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? <LoadingSkeleton /> : (
            <div className="space-y-2">
              {filtered.map(c => {
                const thumbUrl = c.thumbnail_url
                  ? (c.thumbnail_url.startsWith('http') ? c.thumbnail_url : `${process.env.REACT_APP_BACKEND_URL}${c.thumbnail_url}`)
                  : null;
                return (
                <div key={c.id} data-testid={`campaign-card-${c.id}`} onClick={() => onOpenCampaign(c)}
                  className="w-full bg-white rounded-xl border border-slate-100 p-4 hover:border-blue-200 hover:shadow-sm transition-all text-left group cursor-pointer">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {thumbUrl ? (
                        <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Briefcase className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-800 truncate">{c.name}</h3>
                        <StatusBadge status={c.status} />
                        {c.campaign_type && <Badge variant="outline" className="text-[9px] capitalize border-slate-200">{c.campaign_type.replace(/_/g, ' ')}</Badge>}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xs text-slate-400">{c.brand}</span>
                        <span className="text-xs text-slate-300">|</span>
                        <span className="text-xs text-slate-400">{c.deal_count || 0} deals</span>
                        {(c.deals_pending || 0) > 0 && <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">{c.deals_pending} pending</span>}
                        {(c.deals_content_review || 0) > 0 && <span className="text-[10px] text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">{c.deals_content_review} content review</span>}
                        {(c.deals_funded || 0) > 0 && <span className="text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">{c.deals_funded} funded</span>}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      {c.total_value > 0 && <p className="text-sm font-semibold text-slate-700">${c.total_value.toLocaleString()}</p>}
                      <p className="text-[10px] text-slate-400">{c.start_date || 'No dates'}{c.end_date ? ` – ${c.end_date}` : ''}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <span role="button" tabIndex={0} onClick={(e) => duplicateCampaign(e, c.id)} title="Duplicate"
                        data-testid={`dup-campaign-${c.id}`}
                        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-blue-600 transition-colors cursor-pointer">
                        <Copy className="w-3.5 h-3.5" />
                      </span>
                      {c.status !== 'archived' && (
                        <span role="button" tabIndex={0} onClick={(e) => archiveCampaign(e, c.id)} title="Archive"
                          data-testid={`archive-campaign-${c.id}`}
                          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-red-500 transition-colors cursor-pointer">
                          <Archive className="w-3.5 h-3.5" />
                        </span>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors flex-shrink-0" />
                  </div>
                </div>
                )})}
              {filtered.length === 0 && !loading && (
                <div className="text-center py-16 bg-white rounded-xl border border-slate-100">
                  <Briefcase className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm">{search || statusFilter !== 'all' ? 'No matching campaigns' : 'No campaigns yet'}</p>
                  {!search && statusFilter === 'all' && (
                    <Button size="sm" className="mt-3 bg-blue-600 hover:bg-blue-700 text-white text-xs" onClick={() => setShowCreate(true)}>
                      <Plus className="w-3 h-3 mr-1" />Create your first campaign
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ── CAMPAIGN DETAIL VIEW ──
// ═══════════════════════════════════════════════════════════
function CampaignDetailView({ campaign: initialCampaign, onBack, onOpenDeal, onRefresh }) {
  const [campaign, setCampaign] = useState(initialCampaign);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDeal, setShowAddDeal] = useState(false);
  const [connectedCreators, setConnectedCreators] = useState([]);
  const [selectedCreatorId, setSelectedCreatorId] = useState('');
  const [roleDesc, setRoleDesc] = useState('');
  const [addingDeal, setAddingDeal] = useState(false);
  const [setupMode, setSetupMode] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editLevel, setEditLevel] = useState('full');
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportPreset, setSupportPreset] = useState({ category: 'campaign_support', subject: '' });

  const loadCampaign = useCallback(async () => {
    try {
      const res = await API.get(`/buyer/campaigns/${initialCampaign.id}`);
      setCampaign(res.data.campaign);
      setDeals(res.data.campaign.deals || []);
      setEditLevel(res.data.campaign.edit_level || 'full');
      onRefresh(res.data.campaign);
    } catch { toast.error('Failed to load campaign'); }
    finally { setLoading(false); }
  }, [initialCampaign.id, onRefresh]);

  useEffect(() => { loadCampaign(); }, [loadCampaign]);

  const startEdit = () => {
    setEditForm({
      name: campaign.name || '', description: campaign.description || '', objective: campaign.objective || '',
      start_date: campaign.start_date || '', end_date: campaign.end_date || '',
      brand: campaign.brand || '', channels: campaign.channels || [], territory: campaign.territory || [],
      deliverable_types: campaign.deliverable_types || [],
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      const res = await API.put(`/buyer/campaigns/${campaign.id}`, editForm);
      setCampaign(res.data.campaign);
      onRefresh(res.data.campaign);
      setEditing(false);
      toast.success('Campaign updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setEditSaving(false); }
  };

  const archiveCampaign = async () => {
    if (!window.confirm('Archive this campaign? It will be moved to the Archived filter.')) return;
    try {
      await API.delete(`/buyer/campaigns/${campaign.id}`);
      toast.success('Campaign archived');
      onBack();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const duplicateCampaign = async () => {
    try {
      const res = await API.post(`/buyer/campaigns/${campaign.id}/duplicate`);
      toast.success('Campaign duplicated');
      onBack();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openAddDeal = async () => {
    try {
      const res = await API.get('/buyer/connected-creators');
      // Filter out creators already in this campaign's deals
      const existingCreatorIds = deals.map(d => d.creator_id || d.creator?.user_id);
      const available = res.data.connections.filter(c => !existingCreatorIds.includes(c.creator_id));
      setConnectedCreators(available);
    } catch {}
    setSelectedCreatorId('');
    setRoleDesc('');
    setSetupMode(null);
    setShowAddDeal(true);
  };

  const firstDeal = deals.length > 0 ? deals[0] : null;

  const addDeal = async () => {
    if (!selectedCreatorId) { toast.error('Select a creator'); return; }
    if (!setupMode) { toast.error('Choose a setup option'); return; }
    setAddingDeal(true);
    try {
      const payload = {
        creator_id: selectedCreatorId,
        role_description: roleDesc,
        setup_mode: setupMode,
        source_deal_id: setupMode === 'duplicate' && firstDeal ? firstDeal.id : null,
      };
      const res = await API.post(`/buyer/campaigns/${campaign.id}/deals`, payload);
      toast.success('Creator added to campaign');
      setShowAddDeal(false);
      loadCampaign();
      if (res.data.deal) onOpenDeal(res.data.deal);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add deal'); }
    finally { setAddingDeal(false); }
  };

  const dealAmount = (d) => d.proposal?.commercial_offer?.amount || d.proposal?.offered_price || 0;

  return (
    <div data-testid="campaign-detail-view">
      {showAddDeal ? (
        /* ═══ FULL PAGE ADD CREATOR ═══ */
        <div data-testid="add-creator-page">
          <button onClick={() => setShowAddDeal(false)}
            className="flex items-center gap-1 text-slate-400 hover:text-slate-700 text-xs font-medium mb-5 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" />Back to {campaign.name}
          </button>

          <div className="max-w-2xl">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Add Creator to Campaign</h1>
              <p className="text-slate-500 text-sm mt-0.5">{campaign.name} · {campaign.brand}</p>
            </div>

            {connectedCreators.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-100 p-8 text-center">
                <Users className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500 text-sm font-medium">No connected creators available</p>
                <p className="text-slate-400 text-xs mt-1">Connect creators first from the Creators tab, then add them to this campaign.</p>
              </div>
            ) : (
              <>
                {/* Step 1: Select Creator */}
                <div className="bg-white rounded-xl border border-slate-100 p-5 mb-4">
                  <h3 className="font-semibold text-slate-800 text-sm mb-4">Select Creator</h3>
                  <div className="grid grid-cols-1 gap-2 max-h-[240px] overflow-y-auto">
                    {connectedCreators.map(conn => (
                      <button key={conn.creator_id} type="button"
                        onClick={() => { setSelectedCreatorId(conn.creator_id); setSetupMode(null); }}
                        data-testid={`creator-option-${conn.creator_id}`}
                        className={`flex items-center gap-3 p-3 rounded-xl border transition-all text-left
                          ${selectedCreatorId === conn.creator_id ? 'bg-blue-50 border-blue-300 ring-1 ring-blue-200' : 'bg-white border-slate-100 hover:border-blue-200'}`}>
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={conn.creator?.avatar_url} />
                          <AvatarFallback className="bg-blue-100 text-blue-700 text-sm font-bold">{conn.creator?.display_name?.[0]}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 text-sm">{conn.creator?.display_name}</p>
                          <p className="text-xs text-slate-400">{conn.creator?.public_id} · {conn.creator?.bio ? conn.creator.bio.slice(0, 50) + '...' : 'No bio'}</p>
                        </div>
                        {conn.creator?.verification_status === 'verified' && <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
                        {selectedCreatorId === conn.creator_id && <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Step 2: Role */}
                {selectedCreatorId && (
                  <div className="bg-white rounded-xl border border-slate-100 p-5 mb-4">
                    <h3 className="font-semibold text-slate-800 text-sm mb-3">Creator Role</h3>
                    <Input value={roleDesc} onChange={e => setRoleDesc(e.target.value)}
                      placeholder="e.g., Brand ambassador, Product reviewer, Voice talent..."
                      className="h-9 text-sm border-slate-200" />
                    <p className="text-[10px] text-slate-400 mt-1.5">Describe this creator's role in the campaign (optional)</p>
                  </div>
                )}

                {/* Step 3: Deal Setup Mode */}
                {selectedCreatorId && (
                  <div className="bg-white rounded-xl border border-slate-100 p-5 mb-6">
                    <h3 className="font-semibold text-slate-800 text-sm mb-3">Deal Setup</h3>
                    <p className="text-xs text-slate-400 mb-4">Choose how to initialize the proposal for this creator.</p>
                    <div className="grid grid-cols-1 gap-2">
                      {firstDeal && firstDeal.proposal && (
                        <button type="button" data-testid="setup-duplicate" onClick={() => setSetupMode('duplicate')}
                          className={`flex items-center gap-4 p-4 rounded-xl border transition-all text-left
                            ${setupMode === 'duplicate' ? 'bg-blue-50 border-blue-300 ring-1 ring-blue-200' : 'bg-white border-slate-100 hover:border-blue-200'}`}>
                          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                            <Copy className="w-5 h-5 text-blue-500" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-800">Duplicate existing deal</p>
                            <p className="text-xs text-slate-400">Copy proposal structure from {firstDeal.creator?.display_name}'s deal</p>
                          </div>
                        </button>
                      )}
                      <button type="button" data-testid="setup-defaults" onClick={() => setSetupMode('defaults')}
                        className={`flex items-center gap-4 p-4 rounded-xl border transition-all text-left
                          ${setupMode === 'defaults' ? 'bg-blue-50 border-blue-300 ring-1 ring-blue-200' : 'bg-white border-slate-100 hover:border-blue-200'}`}>
                        <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                          <FileText className="w-5 h-5 text-indigo-500" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-800">Use campaign defaults</p>
                          <p className="text-xs text-slate-400">Pre-fill from campaign-level shared defaults</p>
                        </div>
                      </button>
                      <button type="button" data-testid="setup-scratch" onClick={() => setSetupMode('scratch')}
                        className={`flex items-center gap-4 p-4 rounded-xl border transition-all text-left
                          ${setupMode === 'scratch' ? 'bg-blue-50 border-blue-300 ring-1 ring-blue-200' : 'bg-white border-slate-100 hover:border-blue-200'}`}>
                        <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0">
                          <Plus className="w-5 h-5 text-slate-500" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-800">Start from scratch</p>
                          <p className="text-xs text-slate-400">Build the proposal package manually</p>
                        </div>
                      </button>
                    </div>
                  </div>
                )}

                {/* Action */}
                {selectedCreatorId && (
                  <div className="flex items-center gap-3 pb-8">
                    <Button variant="outline" onClick={() => setShowAddDeal(false)} className="border-slate-200 text-sm h-10 px-6">
                      Cancel
                    </Button>
                    <Button data-testid="confirm-add-deal-btn" onClick={addDeal} disabled={addingDeal || !setupMode}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm h-10">
                      {addingDeal ? 'Adding...' : 'Add Creator to Campaign'}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        /* ═══ CAMPAIGN DETAIL ═══ */
        <>
          <button onClick={onBack} className="flex items-center gap-1 text-slate-400 hover:text-slate-700 text-xs font-medium mb-4 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" />Back to Campaigns
          </button>

          {/* Header with edit mode */}
          <div className="bg-white rounded-xl border border-slate-100 p-5 mb-4">
            {editing ? (
              <div className="space-y-4" data-testid="campaign-edit-form">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-slate-700 mb-1 block">Campaign Name</Label>
                    <Input value={editForm.name} onChange={e => setEditForm(f => ({...f, name: e.target.value}))}
                      className="h-9 text-sm border-slate-200" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-700 mb-1 block">
                      Brand {editLevel !== 'full' && <span className="text-[10px] text-slate-400">(locked)</span>}
                    </Label>
                    <Input value={editForm.brand} onChange={e => setEditForm(f => ({...f, brand: e.target.value}))}
                      disabled={editLevel !== 'full'} className="h-9 text-sm border-slate-200" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-slate-700 mb-1 block">Start Date</Label>
                    <Input type="date" value={editForm.start_date} onChange={e => setEditForm(f => ({...f, start_date: e.target.value}))}
                      className="h-9 text-sm border-slate-200" />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-700 mb-1 block">End Date</Label>
                    <Input type="date" value={editForm.end_date} onChange={e => setEditForm(f => ({...f, end_date: e.target.value}))}
                      className="h-9 text-sm border-slate-200" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-700 mb-1 block">Description</Label>
                  <Textarea value={editForm.description} onChange={e => setEditForm(f => ({...f, description: e.target.value}))}
                    rows={2} className="text-sm border-slate-200" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-700 mb-1 block">Objective</Label>
                  <Input value={editForm.objective} onChange={e => setEditForm(f => ({...f, objective: e.target.value}))}
                    className="h-9 text-sm border-slate-200" />
                </div>
                {editLevel === 'limited' && (
                  <div className="flex items-start gap-2 p-2.5 bg-amber-50 rounded-lg border border-amber-200">
                    <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-[11px] text-amber-700">Some fields are locked because proposals have been sent to creators. Channels, territory, and deliverable types cannot be changed.</p>
                  </div>
                )}
                {editLevel === 'locked' && (
                  <div className="flex items-start gap-2 p-2.5 bg-red-50 rounded-lg border border-red-200">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-[11px] text-red-700">This campaign is locked — deals have been accepted. Only status changes are allowed.</p>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button size="sm" onClick={saveEdit} disabled={editSaving} data-testid="save-campaign-edit-btn"
                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8 px-4">
                    {editSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="text-xs h-8">Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h1 className="text-xl font-bold text-slate-900">{campaign.name}</h1>
                    <StatusBadge status={campaign.status} />
                    {campaign.campaign_type && <Badge variant="outline" className="text-[10px] capitalize border-slate-200">{campaign.campaign_type.replace(/_/g, ' ')}</Badge>}
                  </div>
                  <p className="text-sm text-slate-500">{campaign.brand}</p>
                  {campaign.description && <p className="text-xs text-slate-400 mt-1 max-w-xl">{campaign.description}</p>}
                  {campaign.objective && <p className="text-xs text-slate-500 mt-0.5">Objective: {campaign.objective}</p>}
                </div>
                <div className="flex items-center gap-2">
                  {editLevel !== 'locked' && (
                    <Button size="sm" variant="outline" onClick={startEdit} data-testid="edit-campaign-btn"
                      className="text-xs h-8 border-slate-200">
                      <Edit3 className="w-3 h-3 mr-1.5" />Edit
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={duplicateCampaign} data-testid="dup-campaign-detail-btn"
                    className="text-xs h-8 border-slate-200">
                    <Copy className="w-3 h-3 mr-1.5" />Duplicate
                  </Button>
                  {campaign.status !== 'archived' && (
                    <Button size="sm" variant="outline" onClick={archiveCampaign} data-testid="archive-campaign-detail-btn"
                      className="text-xs h-8 border-slate-200 text-red-500 hover:bg-red-50">
                      <Archive className="w-3 h-3 mr-1.5" />Archive
                    </Button>
                  )}
                </div>
              </div>
            )}
            {!editing && (
              <div className="flex gap-1 mt-2 flex-wrap">
                {(campaign.channels || campaign.platforms || []).map(p => <Badge key={p} variant="outline" className="text-[10px] border-slate-200">{p}</Badge>)}
                {campaign.start_date && <span className="text-[10px] text-slate-400 ml-2">{campaign.start_date} – {campaign.end_date || '...'}</span>}
              </div>
            )}
          </div>

          {/* Stats summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            {[
              { label: 'Total Deals', value: deals.length, color: 'text-slate-700' },
              { label: 'Accepted', value: deals.filter(d => d.proposal_status === 'accepted').length, color: 'text-emerald-600' },
              { label: 'Pending', value: deals.filter(d => ['sent', 'countered'].includes(d.proposal_status)).length, color: 'text-amber-600' },
              { label: 'Funded', value: deals.filter(d => ['funds_secured', 'funded'].includes(d.payment_status)).length, color: 'text-blue-600' },
              { label: 'Total Value', value: `$${deals.reduce((s, d) => s + (d.proposal?.commercial_offer?.amount || d.proposal?.offered_price || 0), 0).toLocaleString()}`, color: 'text-slate-900' },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-xl border border-slate-100 p-3 text-center">
                <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Support / Report buttons */}
          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={() => { setSupportPreset({ category: 'campaign_support', subject: `Campaign support — ${campaign.name}` }); setSupportOpen(true); }}
              data-testid="campaign-support-btn"
              type="button"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 text-slate-500 hover:text-blue-600 hover:border-blue-300 transition-colors">
              <HelpCircle className="w-3 h-3" />Request Support
            </button>
            <button
              onClick={() => { setSupportPreset({ category: 'report_issue', subject: `Campaign issue — ${campaign.name}` }); setSupportOpen(true); }}
              data-testid="campaign-report-btn"
              type="button"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 text-slate-500 hover:text-red-600 hover:border-red-300 transition-colors">
              <AlertCircle className="w-3 h-3" />Report Issue
            </button>
          </div>

          <SupportDialog
            open={supportOpen}
            onOpenChange={setSupportOpen}
            defaultCategory={supportPreset.category}
            defaultSubject={supportPreset.subject}
            context={{ campaign_id: campaign.id, campaign_name: campaign.name }}
            title={supportPreset.category === 'report_issue' ? 'Report a campaign issue' : 'Request campaign support'}
          />

          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-800">Creator Deals <span className="text-slate-400 font-normal">({deals.length})</span></h2>
            <Button data-testid="add-deal-btn" onClick={openAddDeal} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8 px-3">
              <Plus className="w-3 h-3 mr-1" />Add Creator
            </Button>
          </div>

          {loading ? <LoadingSkeleton /> : (
            <div className="space-y-2">
              {deals.map(deal => {
                const amt = deal.proposal?.commercial_offer?.amount || deal.proposal?.offered_price || 0;
                const ps = deal.proposal_status || deal.status || 'draft';
                const payStatus = deal.payment_status;
                return (
                  <button key={deal.id} data-testid={`deal-card-${deal.id}`} onClick={() => onOpenDeal(deal)}
                    className="w-full bg-white rounded-xl border border-slate-100 p-4 hover:border-blue-200 hover:shadow-sm transition-all text-left group">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={deal.creator?.avatar_url} />
                        <AvatarFallback className="bg-blue-100 text-blue-700 text-sm font-bold">{deal.creator?.display_name?.[0]}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-800 text-sm">{deal.creator?.display_name}</p>
                          {deal.creator?.verification_status === 'verified' && <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />}
                        </div>
                        <p className="text-xs text-slate-400">{deal.role_description || 'No role specified'}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <StatusBadge status={ps} />
                          {payStatus && payStatus !== 'none' && (
                            <Badge variant="outline" className={`text-[9px] ${
                              payStatus === 'funds_secured' ? 'bg-blue-50 text-blue-600 border-blue-200' :
                              payStatus === 'paid_out' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' :
                              'bg-slate-50 text-slate-500 border-slate-200'
                            }`}>{payStatus.replace(/_/g, ' ')}</Badge>
                          )}
                        </div>
                        <div className="text-right">
                          {amt > 0 && <p className="text-xs font-semibold text-slate-600">${amt.toLocaleString()}</p>}
                          {deal.updated_at && <p className="text-[10px] text-slate-300">{new Date(deal.updated_at).toLocaleDateString()}</p>}
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors" />
                      </div>
                    </div>
                  </button>
                );
              })}
              {deals.length === 0 && (
                <div className="text-center py-12 bg-white rounded-xl border border-slate-100">
                  <Users className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                  <p className="text-slate-400 text-sm">No creator deals yet</p>
                  <Button size="sm" className="mt-3 bg-blue-600 hover:bg-blue-700 text-white text-xs" onClick={openAddDeal}>
                    <Plus className="w-3 h-3 mr-1" />Add a creator to this campaign
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ── DEAL DETAIL VIEW (Workspace) ──
// ═══════════════════════════════════════════════════════════
function DealDetailView({ dealId, onBack }) {
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [diffViewerOpen, setDiffViewerOpen] = useState(false);
  // Auto-select payment tab if returning from Stripe checkout
  const initialTab = new URLSearchParams(window.location.search).get('payment_success') || new URLSearchParams(window.location.search).get('payment_cancelled') ? 'payment' : 'proposal';
  const [activeTab, setActiveTab] = useState(initialTab);

  const loadDeal = useCallback(async () => {
    try {
      const res = await API.get(`/buyer/deals/${dealId}`);
      setDeal(res.data.deal);
    } catch { toast.error('Failed to load deal'); }
    finally { setLoading(false); }
  }, [dealId]);

  useEffect(() => { loadDeal(); }, [loadDeal]);

  if (loading || !deal) return <LoadingSkeleton />;

  const dealAmount = deal.proposal?.commercial_offer?.amount || deal.proposal?.offered_price || 0;
  const canExport = ['sent', 'countered', 'accepted'].includes(deal.proposal_status);
  const versions = deal.proposal_versions || [];

  const downloadAgreementPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await API.get(`/deals/${deal.id}/agreement.pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const cd = res.headers?.['content-disposition'] || '';
      const match = /filename="?([^"]+)"?/i.exec(cd);
      link.download = match?.[1] || `mylikeness-agreement-${deal.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Agreement downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading || !deal) return <LoadingSkeleton />;

  const tabs = [
    { id: 'proposal', label: 'Proposal', icon: Send },
    { id: 'license', label: 'License', icon: FileCheck },
    { id: 'likeness', label: 'Likeness Pack', icon: Package },
    { id: 'content', label: 'Content Review', icon: Eye },
    { id: 'payment', label: 'Payment', icon: DollarSign },
    { id: 'activity', label: 'Activity', icon: Activity },
  ];

  return (
    <div data-testid="deal-detail-view">
      <div className="flex items-center justify-between mb-4">
        <button onClick={onBack} className="flex items-center gap-1 text-slate-400 hover:text-slate-700 text-xs font-medium transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" />Back to Campaign
        </button>
        <div className="flex items-center gap-2">
          {versions.length >= 2 && (
            <Button variant="outline" size="sm" onClick={() => setDiffViewerOpen(true)}
              data-testid="agency-open-version-diff-btn"
              className="text-xs border-slate-200 text-slate-700 hover:bg-slate-50 h-8">
              <GitCompare className="w-3.5 h-3.5 mr-1.5" /> Compare versions
            </Button>
          )}
          {canExport && (
            <Button variant="outline" size="sm" onClick={downloadAgreementPdf} disabled={downloadingPdf}
              data-testid="agency-download-agreement-pdf-btn"
              className="text-xs border-blue-200 text-blue-700 hover:bg-blue-50 h-8">
              <Download className="w-3.5 h-3.5 mr-1.5" />
              {downloadingPdf ? 'Preparing…' : 'Download PDF'}
            </Button>
          )}
        </div>
      </div>

      {/* Deal Header */}
      <div className="bg-white rounded-xl border border-slate-100 p-5 mb-5">
        <div className="flex items-center gap-4">
          <Avatar className="w-12 h-12">
            <AvatarImage src={deal.creator?.avatar_url} />
            <AvatarFallback className="bg-blue-100 text-blue-700 font-bold">{deal.creator?.display_name?.[0]}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold text-slate-900">{deal.creator?.display_name}</h1>
              {deal.creator?.verification_status === 'verified' && <CheckCircle className="w-4 h-4 text-emerald-500" />}
              <StatusBadge status={deal.status} />
            </div>
            <p className="text-xs text-slate-400 mt-0.5">
              {deal.campaign?.name} · {deal.campaign?.brand}
              {deal.role_description ? ` · ${deal.role_description}` : ''}
            </p>
          </div>
          {dealAmount > 0 && (
            <div className="text-right">
              <p className="text-xl font-bold text-slate-900">${dealAmount.toLocaleString()}</p>
              <p className="text-[10px] text-slate-400">Deal value</p>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0.5 bg-slate-100 rounded-lg p-0.5 mb-5 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} data-testid={`deal-tab-${t.id}`} onClick={() => setActiveTab(t.id)}
            className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all whitespace-nowrap
              ${activeTab === t.id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>
            <t.icon className="w-3.5 h-3.5" />{t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'proposal' && <ProposalTab deal={deal} onRefresh={loadDeal} campaign={deal.campaign} />}
      {activeTab === 'license' && <LicenseTab deal={deal} onRefresh={loadDeal} />}
      {activeTab === 'likeness' && <LikenessPackTab deal={deal} onRefresh={loadDeal} />}
      {activeTab === 'content' && <ContentTab deal={deal} onRefresh={loadDeal} />}
      {activeTab === 'payment' && <PaymentTab deal={deal} onRefresh={loadDeal} />}
      {activeTab === 'activity' && <ActivityTab deal={deal} onRefresh={loadDeal} />}

      <ProposalDiffViewer
        open={diffViewerOpen}
        onClose={() => setDiffViewerOpen(false)}
        versions={versions}
        currentProposal={deal.proposal || {}}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ── CREATORS VIEW — Relationship Manager ──
// ═══════════════════════════════════════════════════════════
function CreatorsView({ onOpenCreator }) {
  const [connections, setConnections] = useState([]);
  const [invites, setInvites] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');
  const [sortBy, setSortBy] = useState('recent_activity');
  const [showConnect, setShowConnect] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [connectMode, setConnectMode] = useState('code');
  const [codeInput, setCodeInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [cRes, sRes] = await Promise.all([
        API.get('/buyer/connected-creators'),
        API.get('/buyer/creators-stats'),
      ]);
      setConnections(cRes.data.connections);
      setInvites(cRes.data.invites || []);
      setStats(sRes.data);
    } catch { toast.error('Failed to load creators'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const lookupCreator = async () => {
    if (!codeInput.trim()) return;
    setPreviewLoading(true);
    try {
      const res = await API.get(`/buyer/creators/_/preview?code=${codeInput.trim()}`);
      setPreview(res.data);
    } catch { setPreview(null); toast.error('Creator not found'); }
    finally { setPreviewLoading(false); }
  };

  const connect = async () => {
    setConnecting(true);
    try {
      const payload = connectMode === 'code' ? { creator_code: codeInput.trim() } : { creator_email: emailInput.trim() };
      if (!payload.creator_code && !payload.creator_email) { toast.error('Enter a code or email'); setConnecting(false); return; }
      const res = await API.post('/buyer/connected-creators', payload);
      if (res.data.status === 'invited') {
        toast.success(res.data.message);
      } else {
        toast.success(`Connected with ${res.data.connection?.creator?.display_name}`);
      }
      setShowConnect(false); setShowInvite(false);
      setCodeInput(''); setEmailInput(''); setPreview(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to connect'); }
    finally { setConnecting(false); }
  };

  const inviteByEmail = async () => {
    if (!emailInput.trim()) { toast.error('Enter an email'); return; }
    setConnecting(true);
    try {
      const res = await API.post('/buyer/connected-creators', {
        creator_email: emailInput.trim(),
        personal_message: inviteMessage.trim() || undefined,
      });
      toast.success(res.data.status === 'invited' ? res.data.message : `Connected with ${res.data.connection?.creator?.display_name}`);
      setShowInvite(false); setEmailInput(''); setInviteMessage('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setConnecting(false); }
  };

  const cancelInvite = async (invId) => {
    try { await API.delete(`/buyer/invites/${invId}`); toast.success('Invite cancelled'); load(); }
    catch { toast.error('Failed'); }
  };

  const archiveCreator = async (connId) => {
    try { await API.put(`/buyer/connected-creators/${connId}`, { status: 'archived' }); toast.success('Creator archived'); load(); }
    catch { toast.error('Failed'); }
  };

  const restoreCreator = async (connId) => {
    try { await API.put(`/buyer/connected-creators/${connId}`, { status: 'active' }); toast.success('Creator restored'); load(); }
    catch { toast.error('Failed'); }
  };

  const openDetail = (conn) => {
    if (onOpenCreator) onOpenCreator(conn);
  };

  // Filtering
  let filtered = connections.filter(c => {
    if (search) {
      const q = search.toLowerCase();
      if (!c.creator?.display_name?.toLowerCase().includes(q) &&
          !c.creator?.public_id?.toLowerCase().includes(q) &&
          !c.creator?.bio?.toLowerCase().includes(q) &&
          !(c.tags || []).some(t => t.includes(q))) return false;
    }
    if (filterStatus === 'active' && c.status !== 'active') return false;
    if (filterStatus === 'archived' && c.status !== 'archived') return false;
    if (filterStatus === 'has_license' && !c.active_licenses) return false;
    if (filterStatus === 'pending_proposal' && !c.pending_proposals) return false;
    if (filterVerified === 'verified' && c.creator?.verification_status !== 'verified') return false;
    if (filterVerified === 'unverified' && c.creator?.verification_status === 'verified') return false;
    return true;
  });

  // Sorting
  filtered.sort((a, b) => {
    if (sortBy === 'name') return (a.creator?.display_name || '').localeCompare(b.creator?.display_name || '');
    if (sortBy === 'recently_connected') return new Date(b.connected_at || 0) - new Date(a.connected_at || 0);
    if (sortBy === 'most_active') return (b.active_campaigns || 0) - (a.active_campaigns || 0);
    if (sortBy === 'most_pending') return ((b.pending_proposals || 0) + (b.pending_approvals || 0)) - ((a.pending_proposals || 0) + (a.pending_approvals || 0));
    // default: recent_activity
    return new Date(b.last_activity_at || b.connected_at || 0) - new Date(a.last_activity_at || a.connected_at || 0);
  });

  const statCards = stats ? [
    { label: 'Connected', value: stats.connected, color: 'text-blue-600 bg-blue-50' },
    { label: 'Pending Invites', value: stats.pending_invites, color: 'text-amber-600 bg-amber-50' },
    { label: 'Active Engagements', value: stats.active_engagements, color: 'text-emerald-600 bg-emerald-50' },
    { label: 'Awaiting Approval', value: stats.awaiting_approval, color: 'text-orange-600 bg-orange-50' },
    { label: 'Active Licenses', value: stats.active_licenses, color: 'text-violet-600 bg-violet-50' },
  ] : [];

  return (
    <div data-testid="creators-view">
      {/* Top Bar */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Creators</h1>
          <p className="text-slate-500 text-sm mt-0.5">Manage creator relationships and partnerships</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="invite-creator-btn" variant="outline" onClick={() => setShowInvite(true)}
            className="border-slate-200 text-xs h-9 px-3">
            <Mail className="w-3.5 h-3.5 mr-1.5" />Invite by Email
          </Button>
          <Button data-testid="connect-creator-btn" onClick={() => setShowConnect(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-9 px-4">
            <Link2 className="w-3.5 h-3.5 mr-1.5" />Connect Creator
          </Button>
        </div>
      </div>

      {/* Summary Strip */}
      {stats && (
        <div className="grid grid-cols-5 gap-3 mb-5">
          {statCards.map(s => (
            <div key={s.label} data-testid={`creator-stat-${s.label.toLowerCase().replace(/ /g, '-')}`}
              className="bg-white rounded-xl border border-slate-100 p-3">
              <p className="text-xl font-bold text-slate-900">{s.value}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Search + Filters + Sort */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input data-testid="creator-search" placeholder="Search name, code, bio, tags..."
            value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9 bg-white border-slate-200 h-9 text-sm" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px] h-9 text-xs border-slate-200 bg-white"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Connected</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
            <SelectItem value="has_license">Active License</SelectItem>
            <SelectItem value="pending_proposal">Pending Proposal</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterVerified} onValueChange={setFilterVerified}>
          <SelectTrigger className="w-[140px] h-9 text-xs border-slate-200 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Verification</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="unverified">Unverified</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[160px] h-9 text-xs border-slate-200 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="recent_activity">Recent Activity</SelectItem>
            <SelectItem value="recently_connected">Recently Connected</SelectItem>
            <SelectItem value="name">Name A-Z</SelectItem>
            <SelectItem value="most_active">Most Active</SelectItem>
            <SelectItem value="most_pending">Most Pending</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Pending Invites */}
      {invites.length > 0 && (
        <div className="mb-4 bg-amber-50 rounded-xl border border-amber-100 p-3">
          <p className="text-xs font-semibold text-amber-700 mb-2">Pending Invites ({invites.length})</p>
          <div className="flex flex-wrap gap-2">
            {invites.map(inv => (
              <div key={inv.id} className="flex items-center gap-2 bg-white rounded-lg border border-amber-200 px-3 py-1.5">
                <Mail className="w-3 h-3 text-amber-500" />
                <span className="text-xs text-slate-600">{inv.email}</span>
                <Badge variant="outline" className="text-[10px] border-amber-200 text-amber-600">Pending</Badge>
                <button onClick={() => cancelInvite(inv.id)} className="text-slate-300 hover:text-red-500 ml-1">
                  <XCircle className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Creator Table */}
      {loading ? <LoadingSkeleton /> : (
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          {filtered.length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-10 h-10 text-slate-200 mx-auto mb-2" />
              <p className="text-slate-400 text-sm">{search || filterStatus !== 'all' ? 'No creators match your filters' : 'No connected creators yet'}</p>
              <p className="text-slate-300 text-xs mt-1">Connect your first creator to start sending proposals and managing licensing workflows.</p>
              {!search && filterStatus === 'all' && (
                <div className="flex gap-2 justify-center mt-3">
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs" onClick={() => setShowConnect(true)}>
                    <Link2 className="w-3 h-3 mr-1" />Connect Creator
                  </Button>
                  <Button size="sm" variant="outline" className="border-slate-200 text-xs" onClick={() => setShowInvite(true)}>
                    <Mail className="w-3 h-3 mr-1" />Invite by Email
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <table className="w-full" data-testid="creators-table">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-2.5 px-4 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Creator</th>
                  <th className="text-left py-2.5 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                  <th className="text-center py-2.5 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Campaigns</th>
                  <th className="text-center py-2.5 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Licenses</th>
                  <th className="text-center py-2.5 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Pending</th>
                  <th className="text-left py-2.5 px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Tags</th>
                  <th className="text-right py-2.5 px-4 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(conn => {
                  const hasAction = conn.pending_proposals > 0 || conn.pending_approvals > 0;
                  return (
                    <tr key={conn.id} data-testid={`creator-row-${conn.id}`}
                      className={`border-b border-slate-50 hover:bg-slate-50/50 transition-colors cursor-pointer ${hasAction ? 'bg-amber-50/30' : ''}`}
                      onClick={() => openDetail(conn)}>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-9 h-9">
                            <AvatarImage src={conn.creator?.avatar_url} />
                            <AvatarFallback className="bg-blue-100 text-blue-700 text-xs font-bold">{conn.creator?.display_name?.[0]}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="text-sm font-medium text-slate-800 truncate">{conn.creator?.display_name}</p>
                              {conn.creator?.verification_status === 'verified' && <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />}
                            </div>
                            <p className="text-[10px] text-slate-400 font-mono">{conn.creator?.public_id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-3" onClick={e => e.stopPropagation()}>
                        <div className="flex flex-col gap-1">
                          <StatusBadge status={conn.status} />
                          {conn.creator?.verification_status !== 'verified' && (
                            <Badge variant="outline" className="text-[9px] border-slate-200 text-slate-400 w-fit">Unverified</Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span className={`text-sm font-semibold ${conn.active_campaigns > 0 ? 'text-blue-600' : 'text-slate-300'}`}>
                          {conn.active_campaigns || 0}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span className={`text-sm font-semibold ${conn.active_licenses > 0 ? 'text-emerald-600' : 'text-slate-300'}`}>
                          {conn.active_licenses || 0}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {conn.pending_proposals > 0 && (
                            <Badge className="bg-amber-100 text-amber-700 text-[9px] px-1.5 py-0">{conn.pending_proposals} prop</Badge>
                          )}
                          {conn.pending_approvals > 0 && (
                            <Badge className="bg-orange-100 text-orange-700 text-[9px] px-1.5 py-0">{conn.pending_approvals} rev</Badge>
                          )}
                          {!conn.pending_proposals && !conn.pending_approvals && <span className="text-slate-300 text-xs">-</span>}
                        </div>
                      </td>
                      <td className="py-3 px-3" onClick={e => e.stopPropagation()}>
                        <div className="flex flex-wrap gap-1 max-w-[140px]">
                          {(conn.tags || []).slice(0, 3).map(tag => (
                            <Badge key={tag} variant="outline" className="text-[9px] border-slate-200 text-slate-500 px-1.5 py-0">{tag}</Badge>
                          ))}
                          {(conn.tags || []).length > 3 && <span className="text-[9px] text-slate-400">+{conn.tags.length - 3}</span>}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" className="h-7 text-[10px] text-slate-500 hover:text-blue-600 px-2"
                            onClick={() => openDetail(conn)}>
                            View
                          </Button>
                          {conn.status === 'active' ? (
                            <Button variant="ghost" size="sm" className="h-7 text-[10px] text-slate-400 hover:text-amber-600 px-2"
                              onClick={() => archiveCreator(conn.id)}>
                              Archive
                            </Button>
                          ) : (
                            <Button variant="ghost" size="sm" className="h-7 text-[10px] text-slate-400 hover:text-emerald-600 px-2"
                              onClick={() => restoreCreator(conn.id)}>
                              Restore
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Connect Creator Dialog ── */}
      <Dialog open={showConnect} onOpenChange={v => { setShowConnect(v); if (!v) { setCodeInput(''); setPreview(null); } }}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="text-slate-900">Connect Creator</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Creator Code</Label>
              <div className="flex gap-2">
                <Input data-testid="connect-code-input" value={codeInput}
                  onChange={e => { setCodeInput(e.target.value.toUpperCase()); setPreview(null); }}
                  placeholder="CREAT-XXXXX" className="h-10 text-sm border-slate-200 font-mono tracking-wider flex-1" />
                <Button variant="outline" onClick={lookupCreator} disabled={previewLoading || !codeInput.trim()}
                  className="h-10 text-xs border-slate-200 px-3">
                  {previewLoading ? '...' : 'Look up'}
                </Button>
              </div>
            </div>

            {preview && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-center gap-3">
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={preview.profile?.avatar_url} />
                    <AvatarFallback className="bg-blue-100 text-blue-700 font-bold">{preview.profile?.display_name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="font-semibold text-slate-800 text-sm">{preview.profile?.display_name}</p>
                      {preview.profile?.verification_status === 'verified' && <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{preview.profile?.bio}</p>
                    {preview.listings?.length > 0 && (
                      <p className="text-[10px] text-blue-600 mt-0.5">{preview.listings.length} active listing(s)</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <Button data-testid="confirm-connect-btn" onClick={connect} disabled={connecting || !codeInput.trim()}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs h-9">
              {connecting ? 'Connecting...' : 'Connect Creator'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Invite by Email Dialog ── */}
      <Dialog open={showInvite} onOpenChange={v => { setShowInvite(v); if (!v) { setEmailInput(''); setInviteMessage(''); } }}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="text-slate-900">Invite Creator by Email</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Creator Email</Label>
              <Input data-testid="invite-email-input" type="email" value={emailInput}
                onChange={e => setEmailInput(e.target.value)}
                placeholder="creator@example.com" className="h-10 text-sm border-slate-200" />
              <p className="text-[10px] text-slate-400 mt-1.5">
                If the creator is already on the platform, they will be connected immediately.
                Otherwise, a branded invitation email will be sent.
              </p>
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-1 block">Personal message <span className="text-slate-400">(optional)</span></Label>
              <Textarea data-testid="invite-message-input" value={inviteMessage}
                onChange={e => setInviteMessage(e.target.value)}
                placeholder="e.g. Hi! We'd love to work with you on our upcoming campaign..."
                rows={3} className="text-sm border-slate-200" maxLength={500} />
              <p className="text-[10px] text-slate-400 mt-1 text-right">{inviteMessage.length}/500</p>
            </div>
            <Button data-testid="confirm-invite-btn" onClick={inviteByEmail} disabled={connecting || !emailInput.trim()}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs h-9">
              {connecting ? 'Sending...' : 'Send Invitation'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ── CREATOR FULL DETAIL VIEW (replaces drawer) ──
// ═══════════════════════════════════════════════════════════
function CreatorFullDetailView({ creator, onBack }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editNotes, setEditNotes] = useState(creator.notes || '');
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await API.get(`/buyer/creators/${creator.creator_id}/detail`);
        setDetail(res.data);
        setEditNotes(res.data.connection?.notes || '');
      } catch { toast.error('Failed to load creator details'); }
      finally { setLoading(false); }
    })();
  }, [creator.creator_id]);

  const profile = detail?.profile || creator.creator || {};
  const rel = detail?.relationship || {};
  const conn = detail?.connection || creator;
  const tags = conn.tags || creator.tags || [];
  const permissions = detail?.permissions || [];

  const saveNotes = async () => {
    try {
      await API.put(`/buyer/connected-creators/${creator.id}`, { notes: editNotes });
      toast.success('Notes saved');
    } catch { toast.error('Failed'); }
  };

  const addTag = async () => {
    if (!tagInput.trim()) return;
    const tag = tagInput.trim().toLowerCase();
    if (tags.includes(tag)) { setTagInput(''); return; }
    const newTags = [...tags, tag];
    try {
      await API.put(`/buyer/connected-creators/${creator.id}`, { tags: newTags });
      setDetail(prev => prev ? { ...prev, connection: { ...prev.connection, tags: newTags } } : prev);
      setTagInput('');
    } catch { toast.error('Failed'); }
  };

  const removeTag = async (tag) => {
    const newTags = tags.filter(t => t !== tag);
    try {
      await API.put(`/buyer/connected-creators/${creator.id}`, { tags: newTags });
      setDetail(prev => prev ? { ...prev, connection: { ...prev.connection, tags: newTags } } : prev);
    } catch { toast.error('Failed'); }
  };

  if (loading) return <LoadingSkeleton />;

  return (
    <div data-testid="creator-detail-view">
      <button onClick={onBack} className="flex items-center gap-1 text-slate-400 hover:text-slate-700 text-xs font-medium mb-4 transition-colors">
        <ArrowLeft className="w-3.5 h-3.5" />Back to Creators
      </button>

      {/* Profile header */}
      <div className="bg-white rounded-xl border border-slate-100 p-6 mb-5">
        <div className="flex items-start gap-5">
          <Avatar className="w-20 h-20">
            <AvatarImage src={profile.avatar_url} />
            <AvatarFallback className="bg-blue-100 text-blue-700 font-bold text-2xl">{profile.display_name?.[0]}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-bold text-slate-900">{profile.display_name}</h1>
              {profile.verification_status === 'verified' && <CheckCircle className="w-5 h-5 text-emerald-500" />}
              <StatusBadge status={conn.status || creator.status} />
            </div>
            <p className="text-sm text-slate-400 font-mono">{profile.public_id}</p>
            {profile.bio && <p className="text-sm text-slate-600 mt-2 max-w-2xl">{profile.bio}</p>}
            {profile.niches?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {profile.niches.map(n => <Badge key={n} variant="outline" className="text-xs border-slate-200">{n}</Badge>)}
              </div>
            )}
            {profile.social_links && Object.keys(profile.social_links).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {Object.entries(profile.social_links).map(([k, v]) => v && (
                  <Badge key={k} variant="outline" className="text-[10px] border-slate-200 text-slate-500 capitalize">{k}: {v}</Badge>
                ))}
              </div>
            )}
          </div>
          <div className="text-right text-xs text-slate-400 flex-shrink-0">
            <p>Connected {new Date(conn.connected_at || creator.connected_at).toLocaleDateString()}</p>
            {conn.connection_method && <p className="mt-0.5">via {conn.connection_method}</p>}
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
        {[
          { label: 'Engagements', value: rel.total_engagements || 0, color: 'text-blue-600' },
          { label: 'Active Campaigns', value: rel.active_campaigns || 0, color: 'text-emerald-600' },
          { label: 'Completed', value: rel.completed_deals || 0, color: 'text-slate-600' },
          { label: 'Pending Proposals', value: rel.pending_proposals || 0, color: 'text-amber-600' },
          { label: 'Pending Approvals', value: rel.pending_approvals || 0, color: 'text-orange-600' },
          { label: 'Active Licenses', value: rel.active_licenses || 0, color: 'text-violet-600' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-slate-100 p-4 text-center">
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left column — Licensing preferences + Languages */}
        <div className="lg:col-span-2 space-y-5">
          {permissions.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-100 p-5">
              <h3 className="text-sm font-bold text-slate-800 mb-3">Licensing Preferences</h3>
              <div className="space-y-3">
                {permissions.map((p, i) => (
                  <div key={i} className="p-4 bg-slate-50 rounded-lg">
                    <p className="font-semibold text-slate-700 text-sm mb-2">{p.listing_title}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      {p.allowed_territories_json?.length > 0 && (
                        <div><span className="text-slate-400">Territory:</span> <span className="text-slate-600">{p.allowed_territories_json.join(', ')}</span></div>
                      )}
                      {p.banned_categories_json?.length > 0 && (
                        <div><span className="text-slate-400">Excluded:</span> <span className="text-slate-600">{p.banned_categories_json.join(', ')}</span></div>
                      )}
                      <div><span className="text-slate-400">Endorsement:</span> <span className="text-slate-600">{p.endorsement_allowed_default ? 'Allowed' : 'Requires approval'}</span></div>
                      <div><span className="text-slate-400">Approval:</span> <span className="text-slate-600">{p.approval_mode}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {rel.last_payment && (
            <div className="bg-white rounded-xl border border-slate-100 p-5">
              <h3 className="text-sm font-bold text-slate-800 mb-2">Payment History</h3>
              <p className="text-sm text-slate-600">Last payment: <span className="font-semibold">${rel.last_payment.amount}</span> on {new Date(rel.last_payment.created_at).toLocaleDateString()}</p>
            </div>
          )}

          {profile.languages?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-100 p-5">
              <h3 className="text-sm font-bold text-slate-800 mb-2">Languages</h3>
              <div className="flex flex-wrap gap-2">
                {profile.languages.map(l => <Badge key={l} variant="outline" className="text-xs border-slate-200">{l}</Badge>)}
              </div>
            </div>
          )}
        </div>

        {/* Right column — Tags + Notes + Actions */}
        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-slate-100 p-5">
            <h3 className="text-sm font-bold text-slate-800 mb-3">Tags</h3>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {tags.map(tag => (
                <span key={tag} className="inline-flex items-center gap-1 bg-slate-100 rounded-md px-2.5 py-1 text-xs text-slate-600">
                  {tag}
                  <button onClick={() => removeTag(tag)} className="text-slate-400 hover:text-red-500"><XCircle className="w-3 h-3" /></button>
                </span>
              ))}
              {tags.length === 0 && <span className="text-xs text-slate-300">No tags yet</span>}
            </div>
            <div className="flex gap-1.5">
              <Input data-testid="tag-input" value={tagInput} onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addTag()}
                placeholder="Add tag..." className="h-8 text-xs border-slate-200 flex-1" />
              <Button variant="outline" size="sm" onClick={addTag} className="h-8 text-xs border-slate-200 px-3">Add</Button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-100 p-5">
            <h3 className="text-sm font-bold text-slate-800 mb-3">Internal Notes</h3>
            <Textarea data-testid="creator-notes" value={editNotes} onChange={e => setEditNotes(e.target.value)}
              placeholder="Communication style, restrictions, account notes..."
              rows={4} className="text-xs border-slate-200 bg-slate-50" />
            <Button data-testid="save-creator-notes-btn" size="sm" onClick={saveNotes}
              className="mt-2 bg-blue-600 hover:bg-blue-700 text-white text-xs h-8">Save Notes</Button>
          </div>

          <div className="bg-white rounded-xl border border-slate-100 p-5">
            <h3 className="text-sm font-bold text-slate-800 mb-3">Quick Actions</h3>
            <div className="space-y-2">
              <Button variant="outline" className="w-full justify-start h-9 text-xs border-slate-200">
                <Plus className="w-3 h-3 mr-2 text-blue-600" />Add to Campaign
              </Button>
              {(rel.active_licenses || 0) > 0 && (
                <Button variant="outline" className="w-full justify-start h-9 text-xs border-slate-200">
                  <FileCheck className="w-3 h-3 mr-2 text-violet-600" />View Active Licenses ({rel.active_licenses})
                </Button>
              )}
              {(rel.pending_proposals || 0) > 0 && (
                <Button variant="outline" className="w-full justify-start h-9 text-xs border-slate-200">
                  <Clock className="w-3 h-3 mr-2 text-amber-600" />View Pending Proposals ({rel.pending_proposals})
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ── LICENSES VIEW ──
// ═══════════════════════════════════════════════════════════
function LicensesView() {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await API.get('/buyer/licenses');
        setLicenses(res.data.licenses);
      } catch { toast.error('Failed to load licenses'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <LoadingSkeleton />;

  return (
    <div data-testid="licenses-view">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Licenses</h1>
        <p className="text-slate-500 text-sm mt-0.5">{licenses.length} total licenses</p>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <p className="text-2xl font-bold text-emerald-600">{licenses.filter(l => l.status === 'active').length}</p>
          <p className="text-xs text-slate-400">Active</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <p className="text-2xl font-bold text-amber-600">{licenses.filter(l => l.status === 'pending_payment').length}</p>
          <p className="text-xs text-slate-400">Pending Payment</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-4">
          <p className="text-2xl font-bold text-slate-600">{licenses.length}</p>
          <p className="text-xs text-slate-400">Total</p>
        </div>
      </div>

      <div className="space-y-2">
        {licenses.map(lic => (
          <div key={lic.id} data-testid={`license-card-${lic.license_id}`}
            className="bg-white rounded-xl border border-slate-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <code className="text-blue-600 text-xs font-mono bg-blue-50 px-2 py-0.5 rounded">{lic.license_id}</code>
                <StatusBadge status={lic.status} />
              </div>
              <p className="font-semibold text-slate-700">${lic.amount?.toLocaleString()}</p>
            </div>
            <div className="grid grid-cols-4 gap-3 text-xs p-3 bg-slate-50 rounded-lg mb-3">
              <div><span className="text-slate-400 block">Brand</span><span className="text-slate-700 font-medium">{lic.terms_json?.brand_name}</span></div>
              <div><span className="text-slate-400 block">Creator</span><span className="text-slate-700 font-medium">{lic.terms_json?.creator_name}</span></div>
              <div><span className="text-slate-400 block">Start</span><span className="text-slate-700 font-medium">{lic.starts_at}</span></div>
              <div><span className="text-slate-400 block">End</span><span className="text-slate-700 font-medium">{lic.ends_at}</span></div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="border-slate-200 text-xs h-7"
                onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/verify/${lic.license_id}`); toast.success('Link copied'); }}>
                <Link2 className="w-3 h-3 mr-1" />Verify Link
              </Button>
            </div>
          </div>
        ))}
        {licenses.length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-100">
            <FolderOpen className="w-10 h-10 text-slate-200 mx-auto mb-2" />
            <p className="text-slate-400 text-sm">No licenses yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ── SETTINGS VIEW ──
// ═══════════════════════════════════════════════════════════
// ── Shared Components ──
function InfoCell({ label, value }) {
  return (
    <div>
      <p className="text-[10px] text-slate-400 mb-0.5">{label}</p>
      <p className="text-sm text-slate-700 font-medium">{value || '—'}</p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="h-8 bg-slate-100 rounded-lg w-48" />
      <div className="grid grid-cols-4 gap-4">
        {[1,2,3,4].map(i => <div key={i} className="h-24 bg-slate-100 rounded-xl" />)}
      </div>
      <div className="h-48 bg-slate-100 rounded-xl" />
    </div>
  );
}
